import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import { DevTools } from "components/DevTools";
import { Outlet, useNavigate, useOutletContext } from "react-router-dom";
import { Announcement } from "components/Announcement";
import { RegisterConfirmModal } from "components/RegisterConfirmModal";
import {
  useNoTicketAlert,
  useTournamentDetails,
  useGame,
  useStorage,
  useSummary,
} from "hooks";
import { useStore } from "utils/store";
import { InsufficientBalanceModal } from "components/InsufficientBalanceModal";
import { MessageModal } from "components/MessageModal";
import { BuyIn, GameState, LayoutContext } from "types";
import { Loading } from "components/Loading";
import { useImages } from "hooks/image";
import { useSoundStore } from "utils/sound-store";
import { useLocation } from "react-router-dom";
import { useSkinnedAssets } from "hooks/skin";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { getConfig } from "utils/config";
import { OverlayerControls } from "components/OverlayerControls";

const Container = styled.div`
  width: 100vw;
  height: 100svh;
  position: absolute;

  @supports (height: 100dvh) {
    height: 100dvh;
  }
`;

enum Location {
  Lobby = "/lobby",
  Game = "/game",
  Maintenance = "/maintenance",
}

export const useLayout = () => {
  return useOutletContext<LayoutContext>();
};

export const Layout = () => {
  const [isReady, setIsReady] = useState(false);
  const [isRejoin, setIsRejoin] = useState(false);
  const { buyIn, tournamentId, gameState, buyIns } = useTournamentDetails();
  const navigate = useNavigate();
  const alertNoTicket = useNoTicketAlert();
  const successEnd = useStore((state) => state.successEnd);
  const endedRef = useRef(false);
  const setEnd = useStore((state) => state.setEnd);
  const {
    loadImages,
    preload: preloadGame,
    preloadApngs,
    loaded: imagesLoaded,
  } = useImages();
  const { setGame, setBet } = useGame();
  const stopAll = useSoundStore((state) => state.stopAll);
  const location = useLocation();
  const pathRef = useRef("");
  const lobbyPreloading = useRef(false);
  const storage = useStorage();
  const isPlayAgain = useRef(false);
  const getPayouts = useStore((state) => state.getPayouts);
  const { totalWinnings } = useSummary();
  const { bgDesktop, bgMobile, logo } = useSkinnedAssets();
  const showRegConfirm = useStore((state) => state.showRegConfirm);
  const setShowRegConfirm = useStore((state) => state.setShowRegConfirm);
  const showInsufficient = useStore((state) => state.showInsufficient);
  const setShowInsufficient = useStore((state) => state.setShowInsufficient);
  const setBuyIn = useStore((state) => state.setBuyIn);
  const setShowScratchCard = useStore((state) => state.setShowScratchCard);
  const setReturnUrl = useStore((state) => state.setReturnUrl);
  const { sessionId } = useStorage();

  // BH-438: Create a ref that will hold the selected buy-in option (fixing the play again issue)
  const selectedBuyInFromModal = useRef<BuyIn | null>(null);
  
  const { isPending, data: balance, refetch } = useQuery({
    queryKey: ["balance"],
    queryFn: async () => {
      const result = await axios.get(getConfig("REACT_APP_BALANCE") || "", {
        params: {
          sessionId: sessionId,
        },
      });

      return result.data.balance;
    },
  });

  useEffect(() => {
    if (location.pathname !== pathRef.current) {
      refetch();
    }
  }, [location.pathname, refetch]);

  const preloadLobby = useCallback(() => {
    if (lobbyPreloading.current) return;
    lobbyPreloading.current = true;
    const images = [
      "images/poker-scratch.png",
      "images/one-hero.png",
      "images/icon-close.svg",
      "images/audio.svg",
      "images/close.webp",
      "images/arrow-down.webp",
      logo,
      bgDesktop,
      bgMobile,
    ];
    Promise.allSettled(loadImages(images)).then((results) => {
      if (results.some((value) => value.status === "rejected")) {
        setTimeout(preloadLobby, 1000);
      } else {
        setIsReady(true);
      }
    });
  }, [bgDesktop, bgMobile, loadImages, logo]);

  const loadComplete = useMemo(() => {
    return imagesLoaded && !!tournamentId;
  }, [imagesLoaded, tournamentId]);

  const totalBalance = useMemo(() => {
    return (balance || 0) + (location.pathname === Location.Lobby ? 0 : totalWinnings);
  }, [balance, location.pathname, totalWinnings]);

  useEffect(() => {
    if (loadComplete) setIsReady(true);
  }, [loadComplete]);

  useEffect(() => {
    if (isReady || location.pathname === Location.Game) preloadGame();
    if (isReady && location.pathname === Location.Game) preloadApngs();
  }, [isReady, location.pathname, preloadApngs, preloadGame]);

  useEffect(() => {
    if (successEnd && endedRef.current && location.pathname === Location.Game) {
      navigate(Location.Lobby, { replace: true, state: storage });
    }
  }, [location.pathname, navigate, storage, successEnd]);

  useEffect(() => {
    if (showRegConfirm && totalBalance < buyIn.buyIn) {
      setShowRegConfirm(false);
      setShowInsufficient(true);
    } else if (showInsufficient && totalBalance >= buyIn.buyIn) {
      setShowInsufficient(false);
      setShowRegConfirm(true);
    }
  }, [
    buyIn.buyIn,
    setShowInsufficient,
    setShowRegConfirm,
    showInsufficient,
    showRegConfirm,
    totalBalance,
  ]);

  const handleGame = () => {
    setIsReady(false);
    setShowRegConfirm(false);
    setIsRejoin(false);
    if (location.pathname === Location.Lobby) {
      navigate(Location.Game, {
        replace: true,
      });
    } else {
      if (successEnd) {
        navigate(Location.Lobby, { replace: true, state: storage });
      } else {
        endedRef.current = true;
        setEnd();
      }
    }
  };

  const handleConfirm = (selectedBuyIn: BuyIn) => {
    if (totalBalance < selectedBuyIn.buyIn) {
      setShowInsufficient(true);
      return;
    }

    setShowScratchCard(false);
    setBuyIn(selectedBuyIn);

    // BH-438: Store the selected buy-in option in the ref (fixing the play again issue)
    selectedBuyInFromModal.current = selectedBuyIn;

    isPlayAgain.current = location.pathname === Location.Game;
    handleGame();
  };

  const handlePlay = (rejoin?: boolean) => {
    if (!rejoin && totalBalance < buyIn.buyIn) {
      setShowInsufficient(true);
      return;
    }

    if (rejoin && !buyIns.some((b) => totalBalance >= b.buyIn)) {
      setShowInsufficient(true);
      return;
    }

    setShowRegConfirm(true);
    setIsRejoin(!!rejoin);
  };

  const handleMessageClose = () => {
    alertNoTicket.hide();
    navigate(Location.Lobby, { replace: true, state: storage });
  };

  useEffect(() => {
    if (location.pathname !== pathRef.current) {
      stopAll();
      if (location.pathname !== Location.Maintenance) getPayouts();
    }
  }, [getPayouts, location.pathname, stopAll]);

  const isDown = useMemo(() => {
    return location.pathname === Location.Maintenance;
  }, [location.pathname]);

  useEffect(() => {
    if (isDown) {
      pathRef.current = location.pathname;
      setIsReady(true);
    } else if (
      location.pathname.length > 1 &&
      pathRef.current !== location.pathname &&
      (location.pathname === Location.Lobby || !gameState)
    ) {
      pathRef.current = location.pathname;
      endedRef.current = false;
      const state =
        location.pathname === Location.Game
          ? { ...storage }
          : { ...location.state };
      setGame(state.gameId, state.sessionId);

      // BH-438: If there's a selected buy-in option stored in the ref, use it to set the buy-in state (for fixing the play again issue)
      // This should let the buyIn "survive" the reset operation in setGame
      if (selectedBuyInFromModal.current) {
        setBuyIn(selectedBuyInFromModal.current);
        selectedBuyInFromModal.current = null; // Clear the ref after using it
      }

      if (state.returnUrl) {
        setReturnUrl(state.returnUrl);
      }
    }
  }, [gameState, isDown, location.pathname, location.state, setGame, setReturnUrl, storage]);

  useEffect(() => {
    if (location.pathname === Location.Lobby)
      setIsReady(!!buyIns.length && !isPlayAgain.current);
  }, [buyIns.length, location.pathname]);

  useEffect(() => {
    if (
      gameState === GameState.NewGame &&
      location.pathname === Location.Game &&
      !tournamentId
    ) {
      isPlayAgain.current = false;
      pathRef.current = location.pathname;
      setBet();
    } else if (gameState === GameState.UnfinishedGame) {
      if (location.pathname === Location.Lobby && !successEnd)
        navigate(Location.Game, {
          replace: true,
        });
      else if (location.pathname === Location.Game)
        pathRef.current = location.pathname;
    }
  }, [
    gameState,
    location.pathname,
    navigate,
    setBet,
    successEnd,
    tournamentId,
  ]);

  useEffect(() => {
    if (
      gameState === GameState.NewGame &&
      location.pathname === Location.Lobby &&
      !tournamentId
    ) {
      if (isPlayAgain.current)
        navigate(Location.Game, {
          replace: true,
        });
      else if (!lobbyPreloading.current) preloadLobby();
    }
  }, [
    gameState,
    getPayouts,
    location.pathname,
    navigate,
    preloadLobby,
    tournamentId,
  ]);

  return (
    <Container>
      <Outlet
        context={{
          isReady: isReady && !isPending,
          onPlay: handlePlay,
          onConfirm: handleConfirm,
          onLobby: handleGame,
        }}
      />
      <OverlayerControls isDown={isDown} />
      <DevTools />
      <Announcement />
      {showRegConfirm && (
        <RegisterConfirmModal
          show={true}
          rejoin={isRejoin}
          amount={buyIn.buyIn}
          balance={totalBalance}
          onClose={() => setShowRegConfirm(false)}
          onConfirm={handleConfirm}
        />
      )}
      <InsufficientBalanceModal
        balance={totalBalance}
        show={showInsufficient}
        onClose={() => setShowInsufficient(false)}
      />
      <MessageModal
        message={alertNoTicket.message}
        show={!!alertNoTicket.message}
        onClose={handleMessageClose}
      />
      {!isDown && (!isReady || isPending) && <Loading />}
    </Container>
  );
};
